import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiUploadCloud, FiEye, FiDownload, FiTrash2, FiFile, FiImage, FiFileText } from 'react-icons/fi';
import api from '../../services/api';

const DocCenterTab = ({ shipmentId, shipmentType = 'truck' }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const loadDocs = useCallback(async () => {
    if (!shipmentId) return;
    try {
      // shipment_type or the tab lists another module's shipment with
      // the same id — every module numbers its shipments from 1.
      const res = await api.get(`/documents/shipment/${shipmentId}`,
        { params: { shipment_type: shipmentType } });
      setDocuments(res.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  }, [shipmentId, shipmentType]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      const res = await api.post(`/documents/shipment/${shipmentId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { shipment_type: shipmentType },
      });
      setDocuments(prev => [...res.data, ...prev]);
      toast.success(`${files.length} file(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    uploadFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileSelect = (e) => {
    uploadFiles(e.target.files);
    e.target.value = '';
  };

  const handlePreview = (doc) => {
    const token = localStorage.getItem('token');
    window.open(
      `/api/documents/preview/${doc.id}?token=${token}`,
      '_blank'
    );
  };

  const handleDownload = async (doc) => {
    try {
      const res = await api.get(`/documents/download/${doc.id}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_name}"?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      toast.success('File deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${month}-${day}-${year} ${hours}:${mins}`;
  };

  const getFileIcon = (name) => {
    const ext = (name || '').split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
      return <FiImage size={15} className="file-icon file-icon-image" />;
    }
    if (['pdf'].includes(ext)) {
      return <FiFileText size={15} className="file-icon file-icon-pdf" />;
    }
    return <FiFile size={15} className="file-icon file-icon-default" />;
  };

  if (!shipmentId) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
        <p>Please save the shipment first.</p>
      </div>
    );
  }

  return (
    <div className="doc-center-tab">
      <div className="doc-center-layout">
        {/* Left: Upload area */}
        <div className="doc-upload-panel">
          <div className="doc-upload-panel-header">SELECT FILES</div>
          <div
            className={`doc-drop-zone ${dragOver ? 'drag-over' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <FiUploadCloud size={32} className="drop-zone-icon" />
            <p className="drop-zone-text">Drag and drop file(s) here...</p>
          </div>
          <div className="doc-upload-btn-row">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              style={{ display: 'none' }}
            />
            <button
              className="btn-choose-files"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Choose Files'}
            </button>
          </div>
        </div>

        {/* Right: Document list */}
        <div className="doc-list-panel">
          <div className="doc-list-header">
            <span className="doc-list-title">DOCUMENT LIST</span>
            <span className="doc-list-count">{documents.length} file(s)</span>
          </div>

          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
          ) : documents.length === 0 ? (
            <div className="doc-empty">
              <p>No documents uploaded yet.</p>
              <p>Drag files to the upload area or click "Choose Files".</p>
            </div>
          ) : (
            <div className="doc-table-wrapper">
              <table className="doc-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>NAME</th>
                    <th>DATE</th>
                    <th>SIZE</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td className="doc-icon-cell">{getFileIcon(doc.original_name)}</td>
                      <td className="doc-name-cell">
                        <span className="doc-name" title={doc.original_name}>
                          {doc.original_name}
                        </span>
                      </td>
                      <td className="doc-date-cell">{formatDate(doc.created_at)}</td>
                      <td className="doc-size-cell">{formatFileSize(doc.file_size)}</td>
                      <td className="doc-actions-cell">
                        <button
                          className="doc-action-btn doc-action-preview"
                          onClick={() => handlePreview(doc)}
                          title="Preview"
                        >
                          <FiEye size={14} />
                        </button>
                        <button
                          className="doc-action-btn doc-action-download"
                          onClick={() => handleDownload(doc)}
                          title="Download"
                        >
                          <FiDownload size={14} />
                        </button>
                        <button
                          className="doc-action-btn doc-action-delete"
                          onClick={() => handleDelete(doc)}
                          title="Delete"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocCenterTab;
